/**
 * ActivatePage.jsx
 * CCH Care — Auth
 * Last Modified: 2026-03-26
 *
 * Allows trial/expired clients to request full activation.
 * Shows plan options and sends request to super admin.
 * Accessible both authenticated (from dashboard) and via direct link.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { getTrialStatus, requestActivation } from '../../api/auth';
import { COLORS } from '../../config/constants';

const PLANS = [
  {
    id: 'Basic',
    name: 'Basic',
    price: 'Contact us',
    features: ['Up to 30 beds', 'ADL documentation', 'Basic reports', 'Email support'],
  },
  {
    id: 'Professional',
    name: 'Professional',
    price: 'Contact us',
    popular: true,
    features: ['Up to 120 beds', 'ADL documentation', 'Advanced reports & compliance', 'Real-time alerts', 'Priority support', 'Multi-unit support'],
  },
  {
    id: 'Enterprise',
    name: 'Enterprise',
    price: 'Contact us',
    features: ['Unlimited beds', 'Everything in Professional', 'Multi-facility', 'Custom integrations', 'Dedicated account manager', 'SLA guarantee'],
  },
];

export default function ActivatePage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState('Professional');
  const [notes, setNotes] = useState('');
  const [trialStatus, setTrialStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      getTrialStatus().then(setTrialStatus).catch(() => {});
    }
  }, [isAuthenticated]);

  async function handleSubmit() {
    if (!isAuthenticated) {
      navigate('/login?redirect=/activate');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await requestActivation(selectedPlan, notes);
      setSubmitted(true);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  }

  // Already has pending request
  const hasPendingRequest = trialStatus?.activation_request?.status === 'pending';

  return (
    <div style={{
      minHeight: '100vh', background: '#0B1929', color: '#fff', padding: '40px 24px',
    }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <button onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')}
            style={{ background: 'none', border: 'none', color: '#93C5FD', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginBottom: 16 }}>
            ← {isAuthenticated ? 'Back to Dashboard' : 'Back to Home'}
          </button>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.primary, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
            CCH Care
          </div>
          <h1 style={{ fontSize: 32, fontWeight: 800, marginBottom: 8 }}>
            {submitted ? 'Request Submitted!' : 'Activate Your Account'}
          </h1>
          {trialStatus?.is_expired && !submitted && (
            <div style={{
              display: 'inline-block', padding: '8px 20px', borderRadius: 8,
              background: 'rgba(231, 76, 60, 0.15)', border: '1px solid rgba(231, 76, 60, 0.3)',
              color: '#FC8181', fontSize: 14, fontWeight: 600,
            }}>
              Your trial has expired — activate to restore full access
            </div>
          )}
          {trialStatus?.days_remaining > 0 && !submitted && (
            <p style={{ color: '#94A3B8', fontSize: 15 }}>
              {trialStatus.days_remaining} days remaining on your trial
            </p>
          )}
        </div>

        {/* Success State */}
        {submitted && (
          <div style={{
            maxWidth: 500, margin: '0 auto', textAlign: 'center',
            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 20, padding: 40,
          }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>✅</div>
            <p style={{ color: '#94A3B8', fontSize: 15, lineHeight: 1.7, marginBottom: 24 }}>
              Your activation request for the <strong style={{ color: '#fff' }}>{selectedPlan}</strong> plan has been submitted.
              Our team will review your request and contact you within 1 business day.
            </p>
            <button onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')}
              style={{
                padding: '14px 32px', background: COLORS.primary, border: 'none',
                borderRadius: 10, color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer',
              }}>
              {isAuthenticated ? 'Return to Dashboard' : 'Return Home'}
            </button>
          </div>
        )}

        {/* Pending Request State */}
        {hasPendingRequest && !submitted && (
          <div style={{
            maxWidth: 500, margin: '0 auto', textAlign: 'center',
            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 20, padding: 40,
          }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>⏳</div>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Activation Request Pending</h2>
            <p style={{ color: '#94A3B8', fontSize: 15, lineHeight: 1.7, marginBottom: 16 }}>
              You submitted an activation request for the <strong style={{ color: '#fff' }}>{trialStatus.activation_request.plan_requested}</strong> plan
              on {new Date(trialStatus.activation_request.submitted_at).toLocaleDateString()}.
              Our team is reviewing it.
            </p>
            <p style={{ color: '#64748B', fontSize: 14 }}>
              Questions? Email <a href="mailto:healthmanager@clientcarehealth.org" style={{ color: '#93C5FD' }}>healthmanager@clientcarehealth.org</a>
            </p>
          </div>
        )}

        {/* Plan Selection */}
        {!submitted && !hasPendingRequest && (
          <>
            {error && (
              <div style={{
                maxWidth: 600, margin: '0 auto 24px', padding: '12px 16px', borderRadius: 10,
                background: 'rgba(231, 76, 60, 0.15)', border: '1px solid rgba(231, 76, 60, 0.3)',
                color: '#FC8181', fontSize: 14, textAlign: 'center',
              }}>{error}</div>
            )}

            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 20, marginBottom: 32,
            }}>
              {PLANS.map(plan => (
                <div key={plan.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedPlan(plan.id)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setSelectedPlan(plan.id); }}
                  style={{
                    padding: 28, borderRadius: 16, cursor: 'pointer',
                    background: selectedPlan === plan.id
                      ? 'rgba(27, 107, 147, 0.15)'
                      : 'rgba(255,255,255,0.03)',
                    border: selectedPlan === plan.id
                      ? '2px solid rgba(27, 107, 147, 0.5)'
                      : '1px solid rgba(255,255,255,0.08)',
                    transition: 'all 0.2s',
                    position: 'relative',
                  }}>
                  {plan.popular && (
                    <div style={{
                      position: 'absolute', top: -10, right: 16,
                      background: COLORS.primary, color: '#fff',
                      padding: '4px 12px', borderRadius: 6,
                      fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    }}>Most Popular</div>
                  )}
                  <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>{plan.name}</div>
                  <div style={{ fontSize: 14, color: '#93C5FD', fontWeight: 600, marginBottom: 16 }}>{plan.price}</div>
                  {plan.features.map(f => (
                    <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 14 }}>
                      <span style={{ color: '#2ECC71' }}>✓</span>
                      <span style={{ color: '#CBD5E1' }}>{f}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Notes + Submit */}
            <div style={{
              maxWidth: 600, margin: '0 auto',
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 16, padding: 28,
            }}>
              <label htmlFor="activate-notes" style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#93C5FD', marginBottom: 8 }}>
                Notes or questions (optional)
              </label>
              <textarea
                id="activate-notes"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Any additional information or questions for our team..."
                rows={3}
                style={{
                  width: '100%', padding: 14, fontSize: 15, borderRadius: 10,
                  border: '1px solid rgba(255,255,255,0.15)',
                  background: 'rgba(255,255,255,0.06)', color: '#fff',
                  outline: 'none', fontFamily: 'inherit', resize: 'vertical',
                  marginBottom: 20,
                }}
              />
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  width: '100%', padding: 18, border: 'none', borderRadius: 12,
                  background: COLORS.primary, color: '#fff',
                  fontSize: 17, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer',
                  opacity: submitting ? 0.7 : 1,
                }}
              >
                {!isAuthenticated ? 'Sign In to Submit Request'
                  : submitting ? 'Submitting...'
                  : `Request ${selectedPlan} Plan Activation`}
              </button>
              <div style={{ textAlign: 'center', marginTop: 12, fontSize: 13, color: '#64748B' }}>
                No payment required now. Our team will contact you to finalize.
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
